import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { catchError, map, take } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { Empresa } from "../models/empresa.model";

@Injectable({
  providedIn: "root",
})
export class EmpresasService {
  private empresas$ = new BehaviorSubject<Empresa[]>(null);
  public cidadesEmpresas$ = new BehaviorSubject<any[]>([]); // Observable para cidades

  constructor(private http: HttpClient) {}

  public getAll(): Observable<Empresa[]> {
    return this.http.get<Empresa[]>(environment.rotaEmpresas).pipe(
      map((empresas) =>
        empresas.map((empresa) => new Empresa().deserialize(empresa))
      ),
      catchError(() => throwError("Empresa não encontrada."))
    );
  }
  
  public getAll$() {
    this.http
      .get<Empresa[]>(environment.rotaEmpresas)
      .pipe(take(1))
      .subscribe((empresas) => {
        empresas.sort((a, b) => +b.ativa - +a.ativa);
        this.empresas$.next(empresas);
      });
    this.getCidadesEmpresas(); // Chama getCidadesEmpresas após o carregamento das empresas
  }
  // Método para filtrar cidades
  getCidadesEmpresas(): void {
    const cidadesTemp: Set<string> = new Set();

    this.empresas$.subscribe((empresas) => {
      if (empresas) {
        // Verifica se existem empresas
        empresas.forEach((empresa) => {
          if (empresa.cidade) {
            cidadesTemp.add(empresa.cidade); // Adiciona cidades ao Set
          }
        });

        // Converte o Set em um array e adiciona "Todas"
        const cidadesEmpresas = Array.from(cidadesTemp).map((cidade) => ({
          nomeCidade: cidade,
        }));
        cidadesEmpresas.sort((a, b) =>
          a.nomeCidade.localeCompare(b.nomeCidade)
        );
        cidadesEmpresas.unshift({ nomeCidade: "Todas" });

        // Atualiza o BehaviorSubject com as cidades filtradas
        this.cidadesEmpresas$.next(cidadesEmpresas);
      }
    });
  }

  public escutarMudancasEmpresa(): Observable<Empresa[]> {
    return this.empresas$.asObservable();
  }

  public atualizarEmpresas(empresas: Empresa[]) {
    return this.empresas$.next(empresas);
  }

  private updateEmpresaInState(updatedEmpresa: Empresa) {
    this.empresas$.pipe(take(1)).subscribe((empresas) => {
      // Substitui a guia atualizada na lista do BehaviorSubject
      const updatedEmpresas = empresas.map((guia) =>
        guia._id === updatedEmpresa._id ? updatedEmpresa : guia
      );
      // Atualiza o BehaviorSubject com a nova lista
      this.empresas$.next(updatedEmpresas);
    });
  }

  private adicionarEmpresa(empresa: Empresa) {
    const empresasAtuais = this.empresas$.value;
    this.empresas$.next([empresa, ...empresasAtuais]);
  }

  public getMine(id): Observable<any> {
    return this.http.get(environment.rotaEmpresas, id);
  }

  public getProcedimentos(id: number, empresa: Empresa): Observable<Empresa[]> {
    return this.http
      .get<Empresa[]>(
        environment.rotaEmpresas + "/" + id + "/" + empresa.procedimentos
      )
      .pipe(
        map((empresas) =>
          empresas.map((empresa) => new Empresa().deserialize(empresa))
        ),
        catchError(() => throwError("Empresa não encontrada."))
      );
  }

  public edit(updatedEmpresa: Empresa) {
    return this.http
      .patch(
        `${environment.rotaEmpresas}/${updatedEmpresa._id}`,
        updatedEmpresa
      )
      .pipe(
        map(() => {
          // Atualiza a lista localmente após sucesso na atualização do servidor
          this.updateEmpresaInState(updatedEmpresa);
        }),
        catchError((error) => throwError("Erro ao atualizar guia: " + error))
      );
  }

  public create(empresa: Empresa): Observable<any> {
    return this.http.post(environment.rotaEmpresas, empresa).pipe(
      map((novaEmpresa: Empresa) => {
        // Atualiza a lista localmente após sucesso na atualização do servidor
        this.adicionarEmpresa(novaEmpresa);
      }),
      catchError((error) => throwError("Erro ao criar empresa: " + error))
    );
  }

  public delete(empresa: Empresa): Observable<any> {
    return this.http.delete(environment.rotaEmpresas + "/" + empresa._id);
  }
}
